import React, { useEffect, useRef } from "react";
import { Check, Lightbulb, PackagePlus, Plus } from "lucide-react";
import { toast } from "sonner";

import CampoConta from "./CampoConta";
import ResumoLancamento from "./ResumoLancamento";
import SeletorIngrediente from "./SeletorIngrediente";

// Coluna principal: escolher o ingrediente e lançar quantidade e preço em qualquer
// medida — pacotes, unidades, preço por pacote, por unidade ou total. Tudo junto,
// sem alternar nada: preencher um campo completa os outros.
export default function LancarItem({ l, ingredientes, media, emEdicao, aoAdicionar, aoEscolher, aoCancelarEdicao }) {
  const refIng = useRef(null);
  const refQtd = useRef(null);
  const refPreco = useRef(null);
  const refTotal = useRef(null);

  useEffect(() => { if (emEdicao) setTimeout(() => refQtd.current?.focus(), 50); }, [emEdicao]);

  const escolher = (id) => {
    l.escolher(id);
    aoEscolher(id);
    setTimeout(() => refQtd.current?.focus(), 80);
  };

  const adicionar = () => {
    if (!l.ing) { toast.error("Escolha o ingrediente"); refIng.current?.focus(); return; }
    if (!(l.qtd > 0)) { toast.error("Informe a quantidade"); refQtd.current?.focus(); return; }
    if (!(l.total > 0)) { toast.error("Informe o preço ou o total"); refPreco.current?.focus(); return; }
    aoAdicionar(l.item());
    l.limpar();
    setTimeout(() => refIng.current?.focus(), 50);
  };

  const campos = l.medidas;
  const duas = campos.length > 1; // pacotes + unidades (ou quilos + unidades)
  // Classes fixas: o Tailwind não entende classe montada em tempo de execução.
  const grade = duas ? "grid-cols-2 lg:grid-cols-4 2xl:grid-cols-5" : "grid-cols-2 lg:grid-cols-3";
  const totalClasse = duas ? "col-span-2 lg:col-span-4 2xl:col-span-1" : "col-span-2 lg:col-span-1";

  return (
    <section className="h-full overflow-y-auto rounded-xl border bg-card p-4 sm:p-6 space-y-4" data-e2e="lancar-item">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
          <PackagePlus className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h2 className="font-semibold text-lg">{emEdicao ? "Corrigir item" : "Adicionar item"}</h2>
      </div>

      <SeletorIngrediente ref={refIng} ingredientes={ingredientes} ing={l.ing} aoEscolher={escolher} />

      <div className={`grid gap-3 ${grade}`}>
        {campos.map((m, n) => (
          <CampoConta key={`q-${m.id}`} ref={n === 0 ? refQtd : null} id={`compra-qtd-${m.id}`} e2e={`campo-qtd-${m.id}`}
            rotulo={m.campo} valor={l.naMedida(m)} casas={m.peso ? 3 : 2} minimo={0}
            aoMudar={(v) => l.mudarQtd(m, v)} aoEnter={() => refPreco.current?.focus()} destaque={n === 0} />
        ))}
        {campos.map((m, n) => (
          <CampoConta key={`p-${m.id}`} ref={n === 0 ? refPreco : null} id={`compra-preco-${m.id}`} e2e={`campo-preco-${m.id}`}
            rotulo={`Preço p/ ${m.um}`} prefixo="R$" valor={l.precoNaMedida(m)}
            aoMudar={(v) => l.mudarPreco(m, v)} aoEnter={() => refTotal.current?.focus()} />
        ))}
        <CampoConta ref={refTotal} id="compra-total" e2e="campo-total" rotulo="Total do item" prefixo="R$"
          valor={l.total} aoMudar={l.mudarTotal} aoEnter={adicionar} className={totalClasse} destaqueTotal />
      </div>

      <ResumoLancamento l={l} media={media} />

      <button type="button" onClick={adicionar} data-e2e="adicionar-item"
        className="w-full h-12 rounded-lg bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2 hover:bg-emerald-700 active:scale-[0.99] transition">
        {emEdicao ? <Check className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
        {emEdicao ? "Salvar correção" : "Adicionar à compra"}
      </button>

      {emEdicao && (
        <button type="button" onClick={aoCancelarEdicao} className="w-full text-sm text-muted-foreground hover:text-foreground">
          Cancelar correção
        </button>
      )}

      <p className="flex gap-2 text-xs text-muted-foreground">
        <Lightbulb className="w-4 h-4 shrink-0 text-amber-500" />
        <span>Preencha o campo que você tem na nota — os outros se completam sozinhos. Dá para digitar contas, como <b>6*12</b> ou <b>107,64/6</b>, ou usar a calculadora.</span>
      </p>
    </section>
  );
}
